import Debug from 'debug';
import IManagementDriver from '../NativeDevice/Management/IManagementDriver';
import PowerActionType from '@signageos/actions/dist/Device/Power/PowerActionType';
import { fetch } from '../Isomorphic/fetch';
import { NORMALLY, RARELY } from '../Intervals/constants';
import wait from '@signageos/lib/es6/Timer/wait';
import { GetConfigUrlFn } from '../Device/Configuration/deviceConfigurationHelper';

const debug = Debug('@signageos/front-display:BackupRestart');

const LEGACY_RESTART_STATUS_CODE = 299;
const BACKUP_RESTART_STATUS_CODE = 200;

interface IBackupRestartConfirmationResponse {
	confirmationUrl?: string;
}

/**
 * Periodically checks if it should reboot.
 * @param managementDriver
 * @param getConfigUrl
 * @param fetchFn - only for testing purposes, defaults to Isomorphic fetch
 * @param interval - only for testing purposes
 */
export async function startBackupRestartChecking(
	managementDriver: IManagementDriver,
	getConfigUrl: GetConfigUrlFn,
	fetchFn = fetch,
	interval = RARELY,
) {
	while (true) {
		try {
			debug('Waiting %d ms before next backup restart check', interval);
			await wait(interval);
			debug('Checking for backup restart...');
			const { response, timeout } = await fetchBackupRestartUrl(managementDriver, getConfigUrl, fetchFn);

			await evaluateResponseAndPerformReboot(managementDriver, response, timeout, fetchFn);
		} catch (e) {
			console.warn('backup restart check failed', e);
		}
	}
}

async function fetchBackupRestartUrl(managementDriver: IManagementDriver, getConfigUrl: GetConfigUrlFn, fetchFn = fetch) {
	const type = PowerActionType[PowerActionType.BACKUP_RESTART];
	const deviceUid = await managementDriver.getDeviceUid();
	const getBackUpRestartUrl = (uri: string) => uri + '/power-action/' + type + '/' + deviceUid;

	const cachedUrl = await getConfigUrl(fetchFn);
	if (!cachedUrl) {
		throw new Error('Failed to retrieve configUrl');
	}
	const response = await fetchFn(getBackUpRestartUrl(cachedUrl), undefined, NORMALLY);
	return { response, timeout: NORMALLY };
}

async function evaluateResponseAndPerformReboot(
	managementDriver: IManagementDriver,
	response: Response,
	timeout?: number,
	fetchFn = fetch,
) {
	debug('Backup restart response status:', response.status);

	if (response.status === LEGACY_RESTART_STATUS_CODE) {
		await legacyReboot(managementDriver, response, timeout, fetchFn);
	} else if (response.status === BACKUP_RESTART_STATUS_CODE) {
		await newReboot(managementDriver, response, timeout, fetchFn);
	}
}

async function legacyReboot(managementDriver: IManagementDriver, response: Response, timeout?: number, fetchFn = fetch) {
	debug('Performing legacy reboot via URL:', response.url);
	await fetchFn(response.url, { method: 'POST' }, timeout);
	debug('Legacy reboot request sent, rebooting now...');
	await managementDriver.systemReboot();
}

async function newReboot(managementDriver: IManagementDriver, response: Response, timeout?: number, fetchFn = fetch) {
	const responseBody: IBackupRestartConfirmationResponse | null = await response.json();
	debug('Backup restart response body:', responseBody);

	if (responseBody && responseBody.confirmationUrl && URL.canParse(responseBody.confirmationUrl)) {
		debug('Confirming reboot via URL:', responseBody.confirmationUrl);
		const { status, ok } = await fetchFn(responseBody.confirmationUrl, { method: 'POST' }, timeout);
		if (ok) {
			debug('Reboot confirmed, rebooting now...');
			await managementDriver.systemReboot();
		} else {
			debug('Reboot confirmation failed with status:', status);
		}
	}
}
